import { createContext, useContext, useState, useEffect } from "react";
import { predefinedTemplates } from "../Templates/PredefinedTemplates";
import { useBeforeUnload } from "react-router-dom";
import { config } from "@fullcalendar/core/internal";

const initialData = {
  enterprise_id: "",
  logo: "",
  template_id: "",
  navbar: {},
  footer: {},
  header: {},
  galeria: [],
  statusObra: {},
  map: {},
  formulario: {},
  sobreNos: {},
  loc: "",
  sections: [],
  orderedSections: [],
};


function normalizeConfig(config, templateId) {
  if (!config) return initialData;
  return {
    ...initialData,
    ...config,
    enterprise_id: config.enterprise_id || initialData.enterprise_id,
    logo: config.logo || initialData.logo,
    template_id: templateId,
    navbar: config.navbar || initialData.navbar,
    footer: config.footer || initialData.footer,
    map: config.map || initialData.map,
    header: config.header || initialData.header,
    galeria: config.galeria || initialData.galeria,
    statusObra: config.statusObra || initialData.statusObra,
    formulario: config.formulario || initialData.formulario,
    loc: config.loc || initialData.loc,
    sobreNos: config.sobreNos || config.sobrenos || initialData.sobreNos,
    sections: config.sections || initialData.sections,
    orderedSections:
      config.orderedSections || initialData.orderedSections,
  };
}

const WebsiteContext = createContext();

export const WebsiteProvider = ({ children, initialConfig, templateId }) => {
  // Sempre normaliza a configuração inicial
  const defaultConfig =
    normalizeConfig(initialConfig || predefinedTemplates[templateId]?.config || initialData, templateId);
  const [lastPublishedData, setLastPublishedData] = useState(defaultConfig);
  const [showBanner, setShowBanner] = useState(false);
  const [formData, setFormData] = useState(defaultConfig);

  useEffect(() => {
    if (initialConfig) {
      const normalized = normalizeConfig(initialConfig, templateId);
      setFormData(normalized);
      setLastPublishedData(normalized);
    } else if (predefinedTemplates[templateId]?.config) {
      const normalized = normalizeConfig(predefinedTemplates[templateId].config, templateId);
      setFormData(normalized);
      setLastPublishedData(normalized);
    }
  }, [initialConfig, templateId]);

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (showBanner) {
        event.preventDefault();
        event.returnValue = "Existem alterações não salvas. Tem certeza de que deseja sair?";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [showBanner]);

  const updateFormData = (section, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));
  };

  const setFullFormData = (newData) => {
    if (JSON.stringify(newData) !== JSON.stringify(formData)) {
      setFormData(newData);
    }
  };

  const publishChanges = () => {
    setLastPublishedData(formData);
    setShowBanner(false);
    console.log("Dados publicados:", JSON.stringify(formData, null, 2));
  };

  const cancelChanges = () => {
    setFormData(lastPublishedData);
    setShowBanner(false);
  };

  const initFromTemplate = (templateConfig) => {
    const normalized = normalizeConfig(templateConfig, templateId);
    setFormData(normalized);
    setLastPublishedData(normalized);
  };

  const saveConfigurations = (newData) => {
    setFullFormData(newData);
  };

  return (
    <WebsiteContext.Provider
      value={{
        formData,
        updateFormData,
        setFullFormData,
        showBanner,
        setFormData,
        setShowBanner, 
        publishChanges,
        cancelChanges,
        initFromTemplate,
        saveConfigurations,
        templateId,
      }}
    >
      {children}
    </WebsiteContext.Provider>
  );
};

export const useWebsite = () => {
  return useContext(WebsiteContext);
};
